import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { showToast } from "@/components/ui/toast";
import { FormInput } from "@/components/ui/form-input";
import { useForm, Controller } from "react-hook-form";
import { Search, Users, UserCheck } from "lucide-react";

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  user_type: "buyer" | "both";
  created_at: string;
  updated_at: string;
}

interface UserProfileFormData {
  full_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  user_type: "buyer" | "both";
}

export default function UserProfileManager() {
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState<UserProfile | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "buyer" | "both">("all");
  const [searchTerm, setSearchTerm] = useState("");

  const { register, handleSubmit, reset, control, formState: { errors, isSubmitting } } = useForm<UserProfileFormData>();

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      
      // Try the direct query first (should work with admin RLS policy)
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Direct query failed:", error);
        // Fallback to RPC function if direct query fails
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_all_accounts_admin');
        
        if (rpcError) {
          console.error("RPC query failed:", rpcError);
          throw rpcError;
        }
        
        setProfiles(rpcData || []);
      } else {
        setProfiles(data || []);
      }
    } catch (error) {
      console.error("Error loading profiles:", error);
      showToast("Failed to load user profiles. Please ensure you have admin access.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (profile: UserProfile) => {
    setSelectedProfile(profile);
    reset({
      full_name: profile.full_name,
      email: profile.email,
      phone: profile.phone || "",
      job_title: profile.job_title || "",
      user_type: profile.user_type,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateUserType = async (profileId: string, newUserType: "buyer" | "both") => {
    try {
      const { error } = await supabase
        .from("accounts")
        .update({ user_type: newUserType, updated_at: new Date().toISOString() })
        .eq("id", profileId);

      if (error) throw error;
      showToast("User type updated successfully", "success");
      loadProfiles();
    } catch (error) {
      console.error("Error updating user type:", error);
      showToast("Failed to update user type", "error");
    }
  };

  const onSubmit = async (data: UserProfileFormData) => {
    if (!selectedProfile) return;

    try {
      const { error } = await supabase
        .from("accounts")
        .update({
          full_name: data.full_name,
          email: data.email,
          phone: data.phone || null,
          job_title: data.job_title || null,
          user_type: data.user_type,
          updated_at: new Date().toISOString()
        })
        .eq("id", selectedProfile.id);

      if (error) throw error;
      showToast("Profile updated successfully", "success");
      setIsEditDialogOpen(false);
      loadProfiles();
    } catch (error) {
      console.error("Error updating profile:", error);
      showToast("Failed to update profile", "error");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this user profile? This action cannot be undone.")) return;

    try {
      const { error } = await supabase
        .from("accounts")
        .delete()
        .eq("id", id);

      if (error) throw error;
      showToast("User profile deleted successfully", "success");
      loadProfiles();
    } catch (error) {
      console.error("Error deleting profile:", error);
      showToast("Failed to delete user profile", "error");
    }
  };

  const filteredProfiles = profiles.filter(profile => {
    // Filter by user type
    const typeMatch = filter === "all" || profile.user_type === filter;
    
    // Filter by search term (email, phone, or name)
    const searchMatch = !searchTerm || 
      profile.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (profile.phone && profile.phone.includes(searchTerm));
    
    return typeMatch && searchMatch;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">User Profile Management</h2>
          <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>Total: {profiles.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <UserCheck className="h-4 w-4" />
              <span>Buyers: {profiles.filter(p => p.user_type === "buyer").length}</span>
            </div>
            <div className="flex items-center gap-1">
              <UserCheck className="h-4 w-4" />
              <span>Both: {profiles.filter(p => p.user_type === "both").length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <input
            type="text"
            placeholder="Search by name, email, or phone number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium whitespace-nowrap">Filter by type:</label>
          <Select value={filter} onValueChange={(value: "all" | "buyer" | "both") => setFilter(value)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="buyer">Buyer</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3">Loading profiles...</span>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Results count */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredProfiles.length} of {profiles.length} profiles
          </div>

          {/* Profiles list */}
          <div className="grid gap-4">
            {filteredProfiles.map((profile) => (
              <div
                key={profile.id}
                className="p-4 border rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold">{profile.full_name || 'No Name'}</h3>
                      <span
                        className={`px-2 py-1 text-xs rounded-full font-medium ${
                          profile.user_type === "both"
                            ? "bg-green-100 text-green-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {profile.user_type === "both" ? "Buyer & Supplier" : "Buyer"}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p className="flex items-center gap-2">
                        <span className="font-medium">Email:</span>
                        <span>{profile.email || 'No email'}</span>
                      </p>
                      {profile.phone && (
                        <p className="flex items-center gap-2">
                          <span className="font-medium">Phone:</span>
                          <span>{profile.phone}</span>
                        </p>
                      )}
                      {profile.job_title && (
                        <p className="flex items-center gap-2">
                          <span className="font-medium">Job Title:</span>
                          <span>{profile.job_title}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">User Type:</span>
                        <Select
                          value={profile.user_type}
                          onValueChange={(value: "buyer" | "both") => handleUpdateUserType(profile.id, value)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="buyer">Buyer Only</SelectItem>
                            <SelectItem value="both">Buyer & Supplier</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(profile)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(profile.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-3 pt-3 border-t">
                  <div className="flex justify-between">
                    <span>Created: {new Date(profile.created_at).toLocaleDateString()}</span>
                    {profile.updated_at && (
                      <span>Updated: {new Date(profile.updated_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProfiles.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900">No profiles found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? "Try adjusting your search terms." : "No profiles match the selected filter."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Edit Profile Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogDescription>
              Update user profile information and settings.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormInput
              label="Full Name"
              {...register("full_name", { required: "Name is required" })}
              error={errors.full_name}
            />
            <FormInput
              label="Email"
              type="email"
              {...register("email", { required: "Email is required" })}
              error={errors.email}
            />
            <FormInput
              label="Phone Number"
              {...register("phone")}
              error={errors.phone}
            />
            <FormInput
              label="Job Title"
              {...register("job_title")}
              error={errors.job_title}
            />
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">User Type</label>
              <Controller
                control={control}
                name="user_type"
                rules={{ required: "User type is required" }}
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select user type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buyer">Buyer Only</SelectItem>
                      <SelectItem value="both">Buyer & Supplier</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.user_type && (
                <p className="text-sm text-red-500">{errors.user_type.message}</p>
              )}
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update Profile"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
} 